import React from "react"

import { Badge } from "@coval/ui/badge"
import { Tooltip } from "@coval/ui/tooltip"

interface PersonaVersionTagProps {
  /** Persona version number the run executed against (`Run.persona_version`). */
  ranVersion?: number | null
  /** The persona's current (live) version number. */
  currentVersion?: number | null
  /**
   * "full" renders a labelled pill ("Ran with v2 — current is v4"); "compact" renders a small
   * version chip ("v2") for dense surfaces. Both share one tooltip.
   */
  variant?: "full" | "compact"
  className?: string
}

/**
 * A run reflects the persona version it executed against, which can fall behind the live persona
 * under the copy-on-save model. The tag flags that gap so an old run is never misread as the current
 * persona.
 *
 * Stale only when both versions are known and differ — runs whose persona predates versioning carry
 * no version number and render nothing.
 */
export function isPersonaVersionStale(
  ranVersion?: number | null,
  currentVersion?: number | null,
): boolean {
  return (
    ranVersion != null &&
    currentVersion != null &&
    ranVersion !== currentVersion
  )
}

/**
 * Warning indicator shown when a run executed against a stale persona version. Renders nothing when
 * the run is on the persona's current version (see isPersonaVersionStale).
 */
export function PersonaVersionTag({
  ranVersion,
  currentVersion,
  variant = "full",
  className,
}: PersonaVersionTagProps): React.ReactElement | null {
  if (!isPersonaVersionStale(ranVersion, currentVersion)) {
    return null
  }

  const tooltip = `This run used persona v${ranVersion}. The persona is now on v${currentVersion}.`

  if (variant === "compact") {
    return (
      <Tooltip content={tooltip}>
        <Badge variant="warning" className={`shrink-0 ${className ?? ""}`}>
          v{ranVersion}
        </Badge>
      </Tooltip>
    )
  }

  return (
    <Tooltip content={tooltip}>
      <Badge variant="warning" className={className}>
        Ran with v{ranVersion} — current is v{currentVersion}
      </Badge>
    </Tooltip>
  )
}
